import { SOCKET_HOST_DEV, SOCKET_PORT_DEV } from '@sdk/shared/constants';
import { IEXNote, parseNotificationData } from '@sdk/notification/helpers';
import {
  BaseEvent,
  IxEvent,
  IxEventType,
  ParsedOrderJson,
  RawNotification,
} from '@sdk/notification/types';
import { object } from 'zod';
import type { Channel } from 'pusher-js';
import type Pusher from 'pusher-js';
import { IxEnv } from '@sdk/shared/types/sdk.types';

export interface NotificationOptions {
  channelName: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

// TODO: Replace with proper channel type if available from pusher-js typings

// Cache a single dynamic import of pusher-js to avoid repeated optimize cycles
type PusherConstructor = new (key: string, options: Record<string, unknown>) => Pusher;

let cachedPusherCtorPromise: Promise<PusherConstructor> | null = null;

async function loadPusherCtor(withRetry = true): Promise<PusherConstructor> {
  if (!cachedPusherCtorPromise) {
    cachedPusherCtorPromise = import('pusher-js')
      .then(mod => mod.default as unknown as PusherConstructor)
      .catch(async err => {
        // Handle occasional Vite dev "Outdated Optimize Dep" fetch failures by retrying once
        if (withRetry) {
          await new Promise(resolve => setTimeout(resolve, 500));
          return loadPusherCtor(false);
        }
        throw err;
      });
  }
  return cachedPusherCtorPromise;
}

export class NotificationClient {
  private pusher: Pusher | null = null;
  private channel: Channel | null = null;
  private isActive = false;
  private options: NotificationOptions | null = null;

  private appKey: string;
  private host: string;
  private env: IxEnv;
  private eventHandler: (event: IxEvent) => void;

  constructor(appKey: string, host: string, env: IxEnv, eventHandler: (event: IxEvent) => void) {
    this.appKey = appKey;
    this.host = host;
    this.env = env;
    this.eventHandler = eventHandler;
  }
  notify = (event: IxEvent) => {
    this.eventHandler(event);
  };

  connect = async (options: NotificationOptions) => {
    this.options = options;
    if (this.isActive) {
      const state = this.pusher?.connection.state;
      if (state === 'connected' || state === 'connecting') return;
    }
    const normalizedHost = this.host.replace(/^https?:\/\//, '').replace(/\/$/, ''); // remove protocol and trailing slash

    this.cleanup();
    const PusherCtor = await loadPusherCtor();
    this.pusher = new PusherCtor(this.appKey, {
      wsHost: normalizedHost,
      wsPort: this.env === 'dev' ? SOCKET_PORT_DEV : 443,
      wssPort: this.env === 'dev' ? SOCKET_PORT_DEV : 443,
      forceTLS: this.env !== 'dev',
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
      cluster: 'custom',
    });

    this.channel = this.pusher.subscribe(this.options.channelName);
    this.isActive = true;

    this.channel?.bind_global((event: IEXNote, data: RawNotification) => {
      const values = Object.values(IxEventType);
      if (!values.includes(event as IxEventType)) {
        return;
      }
      const parsedData = parseNotificationData(data, event);
      if (parsedData) {
        const ev = {
          type: event,
          data: parsedData,
        };
        this.eventHandler(ev as IxEvent);
      } else {
      }
    });
    this.pusher.connection.bind('connected', () => {
      console.log(
        `[NotificationService] WebSocket connection established for channel: ${this.channel?.name}`,
      );

      this.options?.onConnect?.();
      this.notify({
        type: IxEventType.WEBSOCKET_CONNECTED,
        data: {
          isConnected: true,
          channelName: this.options?.channelName || '',
        },
      });
    });
    this.pusher.connection.bind('disconnected', () => {
      console.log(
        `[NotificationService] WebSocket connection closed for channel: ${this.channel?.name}`,
      );
      this.isActive = false;
      this.options?.onDisconnect?.();
      this.notify({
        type: IxEventType.WEBSOCKET_DISCONNECTED,
        data: {
          isConnected: false,
          channelName: this.options?.channelName || '',
        },
      });
    });
  };

  cleanup() {
    if (this.pusher && this.channel) {
      this.channel.unbind_all();
      this.pusher.unsubscribe(this.options?.channelName || '');
      this.pusher.disconnect();
    }
    this.pusher = null;
    this.channel = null;
    this.isActive = false;
  }

  isConnected() {
    return this.isActive && this.pusher?.connection.state === 'connected';
  }
  getState() {
    return this.pusher?.connection.state;
  }
}

export default NotificationClient;
